let _ = t => t,
  _t;
import { useCallback, useState } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import LanguageNames from 'ui-addon-i18n/constants/LanguageNames';
import styledWithTheme from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { DownloadIcon } from 'foundations-assets/icons/components/DownloadIcon';
import { H2 } from 'foundations-components/transitional/components/H2';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { LanguageIcon } from 'foundations-assets/icons/components/LanguageIcon';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import MobileFilterSingleSelect from '../TracksPage/MobileFilterSingleSelect';
import RequestLanguageButton from './RequestLanguageButton';
import UsageTracker from '../../utils/UsageTracker';
import { FILTER_TYPES } from 'academy-common-lib/constants/TopFilterConstants';
import { AcademyRatingsTypes } from '../../constants/RatingsAndReviewConstants.migrated';
import CardMenuIcon from 'academy-common-lib/features/cardMenu/components/CardMenuIcon';
import { MenuButton, MenuLabel } from 'academy-common-lib/features/cardMenu/components/MobileCardMenu';
import { RouterSelectSingle } from 'academy-common-lib/features/filters/components/RouterSelectSingle';
import { maybePlaylistCardModelWithProgress } from 'academy-common-lib/features/playlist/playlistUtils';
import { HasParentPlaylist, PlaylistNavigationDropdown } from '../../features/playlistContent/index.migrated';
import { DesktopRatingsContainer } from '../../features/ratingsAndReview';
import { useIsHubspotPlaylist } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import { SocialShareDropdown } from '../../features/share/index.migrated';
import { ContentLanguageSwitcher, MobileContentLanguageSwitcher } from './LanguageSwitcher';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LanguageContainer = styledWithTheme.span(_t || (_t = _`
  white-space: nowrap;
`));
const VerticalRuleDivider = props => /*#__PURE__*/_jsx(View, Object.assign({
  as: "span",
  blockSize: "30px",
  borderInlineEndColor: "border-core-default",
  borderInlineEndStyle: "solid",
  borderInlineEndWidth: "100",
  marginInlineEnd: "200",
  position: "relative"
}, props));

/**
 * The percentage of progress before we show the ratings container
 */
const RATINGS_CONTAINER_MIN_PROGRESS_LEVEL = 10;
const LearningPageHeader = ({
  ContentName,
  additionalTrackingData,
  availableLanguages,
  parentTrack,
  item,
  languageLinkedContent,
  studyGuide,
  playlist,
  playlistProgress
}) => {
  const {
    state
  } = useLocation();
  const {
    isLaptop,
    isTablet
  } = useMediaQuery();
  const [backLink, setBackLink] = useState('');
  const {
    id,
    name,
    duration,
    language,
    progress,
    type
  } = parentTrack !== null && parentTrack !== void 0 ? parentTrack : item;
  const visibility = (parentTrack !== null && parentTrack !== void 0 ? parentTrack : item).visibility;
  const progressLevel = progress ? progress.status === 'COMPLETED' ? 100 : (duration - progress.remainingDuration) * 100 / duration : 0;
  const showRatings = !isLaptop && (progressLevel || (progress === null || progress === void 0 ? void 0 : progress.completedPercentage) || 0) >= RATINGS_CONTAINER_MIN_PROGRESS_LEVEL;
  const ratingsReviewType = type === 'TRACK' ? AcademyRatingsTypes.ACADEMY_COURSE : AcademyRatingsTypes.ACADEMY_LESSON;
  const trackDownloadStudyGuide = useCallback(() => UsageTracker.trackStandalone('Learning Center Interaction', {
    action: 'learning-header-study-guide-click',
    trackId: type === 'TRACK' ? id : undefined,
    language: language,
    lessonId: type === 'LESSON' ? id : undefined,
    title: name
  }), [id, type, language, name]);
  if (!backLink && state !== null && state !== void 0 && state.backLocation) {
    setBackLink(state.backLocation);
  }
  const WithIdsRequestLanguageButton = useCallback(props => /*#__PURE__*/_jsx(RequestLanguageButton, Object.assign({
    lessonId: type === 'LESSON' ? id : undefined,
    trackId: type === 'TRACK' ? id : undefined
  }, props)), [id, type]);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlist);
  const ratingsAdditionalTrackingData = Object.assign({}, additionalTrackingData, {
    progressLevel,
    isHubspotPlaylist
  }, playlist && {
    playlistId: playlist.id,
    playlistName: playlist.name
  });
  const playlistCardModel = maybePlaylistCardModelWithProgress(playlist, playlistProgress);
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    borderColor: "border-core-subtle",
    borderRadius: "300",
    borderStyle: "solid",
    borderWidth: "100",
    "data-test-id": "learning-page-header",
    insetBlockStart: "0",
    margin: "0",
    paddingBlock: "400",
    paddingInline: isTablet ? '300' : '1100',
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "row",
      flexWrap: "nowrap",
      inlineSize: "100%",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        flexGrow: 1,
        children: [/*#__PURE__*/_jsx(HasParentPlaylist, {
          children: /*#__PURE__*/_jsx(View, {
            marginBlock: "200",
            children: /*#__PURE__*/_jsx(PlaylistNavigationDropdown, {
              item: {
                id,
                type: type,
                language: language,
                playlistId: playlist === null || playlist === void 0 ? void 0 : playlist.id
              },
              playlist: playlist,
              playlistProgress: playlistProgress
            })
          })
        }), /*#__PURE__*/_jsx(H2, {
          margin: "0",
          minInlineSize: "90%",
          typography: isTablet ? 'body-200' : undefined,
          children: name || /*#__PURE__*/_jsx(SkeletonText, {})
        }), /*#__PURE__*/_jsx(H2, {
          color: "text-core-subtle",
          margin: "0",
          marginInlineStart: "25",
          typography: isTablet ? 'detail-100' : 'body-100',
          children: ContentName
        })]
      }), isTablet ? /*#__PURE__*/_jsx(View, {
        marginInlineEnd: "100",
        children: /*#__PURE__*/_jsx(CardMenuIcon, {
          item: item,
          parentPlaylist: playlistCardModel,
          parentTrack: parentTrack,
          placement: "bottom left",
          renderExtraOptions: setIsOpen => {
            return (
              /*#__PURE__*/
              /* eslint-disable-next-line react/no-unknown-property */
              // @ts-expect-error used in child component
              _jsxs("div", {
                nestedOptions: true,
                children: [!!studyGuide && /*#__PURE__*/_jsx(MenuButton, {
                  "data-test-id": "track-studyguide-button",
                  external: true,
                  href: studyGuide,
                  iconProps: {
                    color: 'icon-core-subtle'
                  },
                  onClick: () => {
                    trackDownloadStudyGuide();
                    setIsOpen(false);
                    window.open(studyGuide, '_blank'); // XAVI: preventDefault in CardMenuIcon is preventing external links from opening. We will remove this once the menu is refactored to use the right components
                  },
                  size: "small",
                  target: "_blank",
                  use: "link",
                  children: /*#__PURE__*/_jsx(MenuLabel, {
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.resourcesList.downloadStudyGuide"
                    })
                  })
                }), /*#__PURE__*/_jsx(MenuButton
                // @ts-expect-error Untyped
                , {
                  subpage: languageLinkedContent ? /*#__PURE__*/_jsx(MobileContentLanguageSwitcher, {
                    defaultValue: language.toUpperCase(),
                    languageLinkedContent: languageLinkedContent,
                    lessonId: type === 'LESSON' ? id : undefined,
                    onSelect: () => setIsOpen(false),
                    trackId: type === 'TRACK' ? id : undefined
                  }) : /*#__PURE__*/_jsx(MobileFilterSingleSelect, {
                    allowList: availableLanguages,
                    defaultItem: language.toUpperCase(),
                    filterType: FILTER_TYPES.LANGUAGE,
                    lessonId: type === 'LESSON' ? id : undefined,
                    onSelect: () => setIsOpen(false),
                    paddingBlock: "300",
                    paddingInline: "500",
                    trackId: type === 'TRACK' ? id : undefined
                  }),
                  use: "unstyled",
                  children: /*#__PURE__*/_jsxs(MenuLabel, {
                    withChevron: true,
                    children: [I18n.text('learningCenter.topFilters.language.filterTitle'), ":", ' ', LanguageNames[language.toLowerCase()]]
                  })
                })]
              })
            );
          }
        })
      }) : /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "nowrap",
        gap: "300",
        inlineSize: "auto",
        justifyContent: "end",
        children: [showRatings && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(DesktopRatingsContainer, {
            additionalTrackingData: ratingsAdditionalTrackingData,
            contentId: id,
            reviewType: ratingsReviewType,
            visibility: visibility
          }), /*#__PURE__*/_jsx(VerticalRuleDivider, {})]
        }), /*#__PURE__*/_jsxs(LanguageContainer, {
          children: [/*#__PURE__*/_jsx(IconHolder, {
            color: "icon-core-subtle",
            icon: LanguageIcon,
            insetBlockStart: "5px",
            padding: "0",
            position: "relative",
            size: "500"
          }), languageLinkedContent ? /*#__PURE__*/_jsx(ContentLanguageSwitcher, {
            defaultValue: language.toUpperCase(),
            languageLinkedContent: languageLinkedContent,
            menuStyle: {
              maxHeight: '270px'
            },
            menuWidth: "auto",
            optionComponent: WithIdsRequestLanguageButton
          }) : /*#__PURE__*/_jsx(RouterSelectSingle, {
            allowList: availableLanguages,
            defaultItem: language.toUpperCase(),
            filterType: FILTER_TYPES.LANGUAGE,
            menuStyles: {
              maxBlockSize: '270px'
            },
            menuWidth: "auto",
            optionComponent: WithIdsRequestLanguageButton,
            paddingInlineEnd: "0 !important",
            trackingKey: "language-switcher"
          })]
        }), /*#__PURE__*/_jsx(VerticalRuleDivider, {
          marginInlineEnd: "100"
        }), type === 'TRACK' && studyGuide && /*#__PURE__*/_jsx(IconButton, {
          "data-test-id": "track-studyguide-button",
          href: studyGuide,
          onClick: trackDownloadStudyGuide,
          placement: "bottom",
          target: "_blank",
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.resourcesList.downloadStudyGuide"
          }),
          use: "link",
          children: /*#__PURE__*/_jsx(IconHolder, {
            color: "icon-core-subtle",
            icon: DownloadIcon,
            padding: "0",
            size: "800"
          })
        }), /*#__PURE__*/_jsx(CardMenuIcon, {
          item: parentTrack !== null && parentTrack !== void 0 ? parentTrack : item,
          placement: "bottom left"
        }), /*#__PURE__*/_jsx(SocialShareDropdown, {
          item: item,
          parentPlaylist: playlistCardModel,
          parentTrack: parentTrack
        })]
      })]
    })
  });
};
export default LearningPageHeader;